import React, { useState, useRef, useEffect } from 'react';
import { apiFetch } from '../lib/api-client';
import HLS from 'hls.js';

interface Props {
  channelId: string;
  channelName: string;
  onClose: () => void;
}

export function VideoPlayer({ channelId, channelName, onClose }: Props) {
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<HLS | null>(null);

  useEffect(() => {
    const resolveStream = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiFetch<{ url: string }>(`/channels/${channelId}/resolve`);
        setStreamUrl(result.url);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to resolve stream';
        setError(message);
        console.error('Error resolving stream:', err);
      } finally {
        setLoading(false);
      }
    };

    resolveStream();
  }, [channelId]);

  useEffect(() => {
    if (!streamUrl || !videoRef.current) return;

    const setupVideo = () => {
      const video = videoRef.current!;
      const isHLS = streamUrl.includes('.m3u8');

      console.log('Setting up video:', { streamUrl, isHLS });

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      if (isHLS && HLS.isSupported()) {
        const hls = new HLS({
          autoStartLoad: true,
          debug: true,
          enableWorker: false,
        });
        hlsRef.current = hls;

        hls.loadSource(streamUrl);
        hls.attachMedia(video);

        hls.on(HLS.Events.MANIFEST_PARSED, () => {
          console.log('HLS manifest parsed, attempting to play');
          video.play().catch((err) => {
            console.warn('Autoplay blocked:', err);
          });
        });

        hls.on(HLS.Events.ERROR, (event, data) => {
          console.error('HLS error:', data);
          if (data.fatal) {
            let errorMsg = 'Stream error: ';
            switch (data.type) {
              case HLS.ErrorTypes.NETWORK_ERROR:
                errorMsg += `Network error - ${data.error?.message || 'Check if URL is accessible'}`;
                break;
              case HLS.ErrorTypes.MEDIA_ERROR:
                errorMsg += `Media error - ${data.error?.message || 'Invalid media format'}`;
                break;
              default:
                errorMsg += data.error?.message || 'Unknown error';
            }
            setError(errorMsg);
          }
        });

        hls.on(HLS.Events.FRAG_LOAD_ERROR, (event, data) => {
          console.warn('Fragment load error:', data);
        });
      } else if (isHLS && video.canPlayType('application/vnd.apple.mpegurl')) {
        console.log('Using native Safari HLS');
        video.src = streamUrl;
        video.play().catch((err) => {
          console.warn('Autoplay blocked:', err);
        });
      } else {
        console.log('Using native video element (non-HLS)');
        video.src = streamUrl;
        video.play().catch((err) => {
          console.warn('Autoplay blocked:', err);
        });
      }
    };

    setupVideo();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [streamUrl]);

  return (
    <div className="video-player-modal">
      <div className="video-player-container">
        <div className="video-header">
          <h3>{channelName}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {loading && <div className="video-loading">Loading stream...</div>}
        {error && <div className="video-error">Error: {error}</div>}
        {streamUrl && (
          <video
            ref={videoRef}
            className="video-element"
            controls
            crossOrigin="anonymous"
          />
        )}
      </div>
    </div>
  );
}
